import React, { useEffect, useMemo, useRef, useState } from "react";
import { getDocument, GlobalWorkerOptions } from "pdfjs-dist";

/**
 * PdfViewer
 * - Lightweight PDF display using the browser's built-in renderer via iframe
 *
 * Props:
 * - fileUrl: string
 * - height?: number (default 480)
 */
const PdfViewer = ({ fileUrl, height = 480 }) => {
  const containerRef = useRef(null);
  const [error, setError] = useState(null);

  const resolvedSrc = useMemo(() => {
    if (!fileUrl) return null;

    if (typeof fileUrl === "string") {
      const trimmed = fileUrl.trim();
      if (trimmed.startsWith("data:application/pdf")) return trimmed;

      const looksLikeBase64 = /^[A-Za-z0-9+/=\r\n]+$/.test(trimmed) && trimmed.length > 100;
      if (looksLikeBase64) {
        return `data:application/pdf;base64,${trimmed.replace(/\s+/g, "")}`;
      }

      return trimmed;
    }

    if (typeof fileUrl === "object" && fileUrl.url) {
      return fileUrl.url;
    }

    return null;
  }, [fileUrl]);

  useEffect(() => {
    if (!resolvedSrc || !containerRef.current) return undefined;

    const container = containerRef.current;
    let destroyed = false;
    setError(null);
    container.innerHTML = "";

    // Align worker with the bundled pdf.js version
    GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";

    const base64ToUint8Array = (base64) => {
      const cleaned = base64.replace(/^data:application\/pdf;base64,/, "").replace(/\s+/g, "");
      const binary = atob(cleaned);
      const len = binary.length;
      const bytes = new Uint8Array(len);
      for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
      return bytes;
    };

    const render = async () => {
      try {
        const isDataUrl = resolvedSrc.startsWith("data:application/pdf;base64,");
        const loadingTask = getDocument(
          isDataUrl ? { data: base64ToUint8Array(resolvedSrc) } : { url: resolvedSrc }
        );

        const pdf = await loadingTask.promise;
        const width = container.clientWidth || 800;

        for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
          if (destroyed) break;
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1 });
          const scale = width / viewport.width;
          const scaledViewport = page.getViewport({ scale });

          const canvas = document.createElement("canvas");
          canvas.width = scaledViewport.width;
          canvas.height = scaledViewport.height;
          canvas.style.display = "block";
          canvas.style.width = "100%";
          canvas.style.marginBottom = "12px";
          container.appendChild(canvas);

          const context = canvas.getContext("2d");
          await page.render({ canvasContext: context, viewport: scaledViewport }).promise;
        }
      } catch (err) {
        if (!destroyed) {
          console.error("PDF render error", err);
          setError("Unable to render PDF");
        }
      }
    };

    render();

    return () => {
      destroyed = true;
      container.innerHTML = "";
    };
  }, [resolvedSrc]);

  if (!resolvedSrc) return null;

  return (
    <div style={{ height, border: "1px solid #f0f0f0", borderRadius: 12, overflow: "hidden" }}>
      {error ? (
        <div style={{ padding: 16, color: "#c00" }}>{error}</div>
      ) : (
        <div ref={containerRef} style={{ width: "100%", height: "100%", overflow: "auto", padding: 8 }} />
      )}
    </div>
  );
};

export default PdfViewer;
